import type { TeacherClass } from "~/features/classes/classes";
import type { TodayOverview, TodayStudent } from "~/features/today/today";
import type { HttpPort } from "~/lib/http";

export interface StudentRecord {
  id: string;
  first_name: string;
  last_name: string;
  joined_at: string;
  monster_name: string | null;
  body_color?: string | null;
  level: number | null;
  happiness?: number | null;
  experience_points: number | null;
  streak: number;
}

export interface StudentStatus {
  checkedIn: boolean;
  checkedOut: boolean;
  absent: boolean;
  mood?: number | null;
  weeklyGoalsRating?: number | null;
  classGoalRating?: number | null;
  taskScore?: number | null;
  taskComment?: string | null;
  taskText?: string | null;
  monsterMood?: string | null;
  monsterMoodCustom?: string | null;
  moodChips?: Array<{ id: string; emoji: string; label: string }> | null;
  taskSkipped?: boolean;
}

export interface StudentCardModel extends StudentRecord {
  status: StudentStatus;
}

export interface StudentsSnapshot {
  class: TeacherClass;
  students: StudentCardModel[];
  classDailyGoal: string | null;
  activeGoals: Array<{ rank: number | null; title: string }>;
}

export interface StudentHistory {
  student: {
    first_name: string;
    last_name: string;
    level: number | null;
    monster_name: string | null;
    experience_points: number | null;
  };
  checkins: Array<{
    checkin_date: string;
    checkin_time: string | null;
    checkout_time: string | null;
    daily_goal: string | null;
    morning_mood: number | null;
    mood: number | null;
    goal_1_rating: number | null;
    class_goal_rating: number | null;
    daily_task_score: number | null;
    daily_task_comment: string | null;
    task_skipped: boolean;
    xp_earned: number;
    task_text: string | null;
    task_short: string | null;
    class_goal_text: string | null;
    weekly_goals: string | null;
  }>;
  proposals: Array<{
    id: string;
    title: string;
    status: "approved" | "rejected" | "pending" | string;
    rejection_reason: string | null;
    created_at: string;
  }>;
  absences: string[];
}

export type StudentCommand =
  | { type: "add"; firstName: string; lastName: string; password: string }
  | { type: "rename"; studentId: string; firstName: string; lastName: string }
  | { type: "toggle-absence"; studentId: string }
  | { type: "remove"; studentId: string; deleteAccount: boolean };

function statusFromToday(studentId: string, today: TodayOverview | null): StudentStatus {
  if (!today) return { checkedIn: false, checkedOut: false, absent: false };
  const checkedOut = today.checked_out_evening.find((student) => student.student_id === studentId);
  if (checkedOut) return mapStatus(checkedOut, true, true);
  const checkedIn = today.checked_in_morning.find((student) => student.student_id === studentId);
  if (checkedIn) return mapStatus(checkedIn, true, false);
  const waiting = today.not_checked_in.find((student) => student.student_id === studentId);
  return { checkedIn: false, checkedOut: false, absent: waiting?.is_absent === true };
}

function mapStatus(student: TodayStudent, checkedIn: boolean, checkedOut: boolean): StudentStatus {
  return {
    checkedIn,
    checkedOut,
    absent: student.is_absent === true,
    mood: student.mood,
    weeklyGoalsRating: student.goal_1_rating,
    classGoalRating: student.class_goal_rating,
    taskScore: student.daily_task_score,
    taskComment: student.daily_task_comment,
    taskText: student.daily_task_text || student.daily_goal || null,
    monsterMood: student.monster_mood,
    monsterMoodCustom: student.monster_mood_custom,
    moodChips: student.mood_chips,
    taskSkipped: student.task_skipped === true
  };
}

export function createStudentsModule(http: HttpPort, token: string) {
  return {
    async load(classId: string): Promise<StudentsSnapshot> {
      const [details, today] = await Promise.all([
        http.request<{ class: TeacherClass; students: StudentRecord[] }>({ path: `/classes/${classId}`, token }),
        http.request<TodayOverview>({ path: `/classes/${classId}/today`, token }).catch(() => null)
      ]);
      return {
        class: details.class,
        students: details.students.map((student) => ({ ...student, status: statusFromToday(student.id, today) })),
        classDailyGoal: today?.class_daily_goal ?? null,
        activeGoals: today?.active_goals ?? []
      };
    },

    history(classId: string, studentId: string): Promise<StudentHistory> {
      return http.request<StudentHistory>({ path: `/classes/${classId}/students/${studentId}/history`, token });
    },

    async execute(classId: string, command: StudentCommand): Promise<{ message?: string }> {
      if (command.type === "add") {
        return http.request({ path: "/students/register-in-class", method: "POST", token, body: { firstName: command.firstName, lastName: command.lastName, password: command.password, classId } });
      }
      if (command.type === "rename") {
        return http.request({ path: `/students/${classId}/${command.studentId}/rename`, method: "PATCH", token, body: { firstName: command.firstName, lastName: command.lastName } });
      }
      if (command.type === "toggle-absence") {
        return http.request({ path: `/classes/${classId}/absences/toggle`, method: "POST", token, body: { studentId: command.studentId } });
      }
      const query = command.deleteAccount ? "?deleteAccount=true" : "";
      return http.request({ path: `/students/${classId}/${command.studentId}${query}`, method: "DELETE", token });
    }
  };
}

export type StudentsModule = ReturnType<typeof createStudentsModule>;
